import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import akLogo from "@/assets/ak-logo.png";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

function makeResetSchema(t: (k: string) => string) {
  return z
    .object({
      password: z
        .string()
        .min(8, t("auth.zod.passwordTooShort"))
        .max(72, t("auth.zod.passwordTooLong"))
        .regex(/[A-Z]/, t("auth.zod.passwordUppercase"))
        .regex(/[a-z]/, t("auth.zod.passwordLowercase"))
        .regex(/[0-9]/, t("auth.zod.passwordNumber")),
      confirm: z.string(),
    })
    .refine((d) => d.password === d.confirm, { message: t("auth.zod.passwordsMismatch"), path: ["confirm"] });
}

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Set a new password · AdKavi" },
      { name: "description", content: "Choose a new password for your AdKavi account." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [ready, setReady] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // PKCE flow: Supabase sends ?code= in the URL
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
        if (error) {
          setInvalid(true);
        } else {
          setReady(true);
          window.history.replaceState({}, "", window.location.pathname);
        }
      });
      return;
    }

    // Implicit / hash-based flow fallback
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = makeResetSchema(t).safeParse({ password, confirm });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: parsed.data.password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("reset.toastDone"));
    navigate({ to: "/dashboard" });
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <img src={akLogo} alt="AdKavi logo" className="w-10 h-10 rounded-xl object-cover" />
          <span className="text-2xl font-bold">AdKavi</span>
        </Link>

        <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur p-8">
          <h1 className="text-2xl font-bold mb-2">{t("reset.title")}</h1>

          {invalid ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("reset.expired")}
              </p>
              <Link to="/forgot-password" className="inline-block text-sm text-primary hover:underline">
                {t("reset.requestNew")}
              </Link>
            </div>
          ) : (
            <>
          <p className="text-sm text-muted-foreground mb-6">
            {ready ? t("reset.descReady") : t("reset.descValidating")}
          </p>

          {ready && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t("reset.newPassword")}</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                <p className="text-xs text-muted-foreground">
                  {t("reset.hint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">{t("reset.confirm")}</Label>
                <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                {loading ? t("reset.updating") : t("reset.update")}
              </Button>
            </form>
          )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}
